import { AbstractStep } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import type { AbstractStepProps } from "@component/assets/AbstractLeadForm/Steps/AbstractStep";
import {
	faCalendarCheck as fasCalendarCheck,
	faArrowRight as fasArrowRight,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { FormCurrency, FormNumber, ProgressButton } from "bm-ui-kit";
import type { ProgressButtonState } from "bm-ui-kit";
import type { FormApi } from "final-form";
import React from "react";
import type { ReactNode } from "react";
import { Card } from "react-bootstrap";
import { MAX_GROSS_INCOME, MIN_GROSS_INCOME } from "../data";

const MIN_AVERAGE_PAYMENT_DEADLINE = 30;
const MAX_AVERAGE_PAYMENT_DEADLINE = 360;

export interface StepFields {
	grossIncome: number;
	averagePaymentDeadline: number;
}

interface Step1Props extends AbstractStepProps {
	submitState: ProgressButtonState;
}

export class Step1 extends AbstractStep<StepFields, Step1Props> {
	protected renderContent(_values: StepFields, form: FormApi<StepFields>): ReactNode {
		const { submitState } = this.props;

		return (
			<>
				<Card.Header className="card-header">
					<Card.Title className="text-center">
						Hitelkeret megállapítása
						<br />a kibocsátott számlákra
					</Card.Title>
				</Card.Header>
				<Card.Body>
					<FormCurrency
						label="Számla bruttó összege"
						name="grossIncome"
						validate={this.validGrossIncome}
						mask="0,00"
						infoTooltip="Le nem járt esedékességű számlák."
						required
					/>
					<FormNumber
						label="Átlagos számla fizetési határidő"
						name="averagePaymentDeadline"
						prepend={<FontAwesomeIcon icon={fasCalendarCheck} />}
						append="nap"
						validate={this.validAveragePaymentDeadline}
						required
					/>
				</Card.Body>
				<Card.Footer className="text-center">
					<ProgressButton
						type="submit"
						icon={<FontAwesomeIcon icon={fasArrowRight} />}
						className={!form.getState().valid ? "disabled" : undefined}
						state={submitState}
					>
						Kalkulálás
					</ProgressButton>
				</Card.Footer>
			</>
		);
	}

	@autobind
	private validGrossIncome(grossIncome: number): string {
		if (isNaN(grossIncome)) {
			return "Kérjük add meg a céged éves bevételét.";
		}

		if (grossIncome < MIN_GROSS_INCOME) {
			return `Az éves árbevétel összege nem lehet kevesebb, mint ${formatText(
				MIN_GROSS_INCOME / 1_000_000,
				"millió ft",
			)}.`;
		}

		if (grossIncome > MAX_GROSS_INCOME) {
			return `Az éves árbevétel összege nem lehet nagyobb, mint ${formatText(
				MAX_GROSS_INCOME / 1_000_000,
				"millió ft",
			)}.`;
		}
	}

	@autobind
	private validAveragePaymentDeadline(averagePaymentDeadline: number): string {
		if (isNaN(averagePaymentDeadline)) {
			return "Az átlagos számlafizetési határidőt meg kell adni!";
		}

		if (averagePaymentDeadline < MIN_AVERAGE_PAYMENT_DEADLINE) {
			return `Az átlagos számlafizetési határidő nem lehet kevesebb mint ${MIN_AVERAGE_PAYMENT_DEADLINE} nap!`;
		}

		if (averagePaymentDeadline > MAX_AVERAGE_PAYMENT_DEADLINE) {
			return `Az átlagos számlafizetési határidő nem lehet több, mint ${MAX_AVERAGE_PAYMENT_DEADLINE} nap`;
		}
	}

	protected getComponentName(): string {
		return "Step1";
	}
}
